import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { CAMERA, FOG_DENSITY, LIGHTING, PALETTE, POST_PROCESSING } from './config';
import { createOutlinePass, updateOutlineResolution } from './shaders/outlinePass';
import { createRageFxPass } from './shaders/rageFx';
import { createCar } from './entities/car';
import { createDuck } from './entities/duck';
import { showCountdown } from './boot/countdown';
import { registerSystem, startGameLoop } from './gameLoop';
import { cameraSystem } from './systems/cameraSystem';
import { chaosSystem } from './systems/chaosSystem';
import { comboSystem } from './systems/comboSystem';
import { copSystem } from './systems/copSystem';
import { dayNightSystem } from './systems/dayNightSystem';
import { ducklingSystem } from './systems/ducklingSystem';
import { duckAnimationSystem } from './systems/duckAnimationSystem';
import { duckEvolutionSystem } from './systems/duckEvolutionSystem';
import { inputSystem } from './systems/inputSystem';
import { juiceSystem } from './systems/juiceSystem';
import { movementSystem } from './systems/movementSystem';
import { objectiveSystem } from './systems/objectiveSystem';
import { particleBurstSystem } from './systems/particleBurstSystem';
import { isPortalEntry, portalSystem } from './systems/portalSystem';
import { multiplayerSystem } from './systems/multiplayerSystem';
import { neonSystem } from './systems/neonSystem';
import { npcSystem } from './systems/npcSystem';
import { initPhysics, physicsSystem } from './systems/physicsSystem';
import { quackSystem } from './systems/quackSystem';
import { photoModeSystem } from './systems/photoModeSystem';
import { tutorialSystem } from './systems/tutorialSystem';
import { vehicleSystem } from './systems/vehicleSystem';
import type { World } from './types';
import { startBackgroundMusic } from './utils/musicSynth';
import { duckPanelSystem } from './ui/duckPanel';
import { hudSystem } from './ui/hud';
import { initNewsTicker } from './ui/newsTicker';
import { initShareButton } from './ui/shareButton';
import { initTouchControls } from './ui/touchControls';
import { CAR_SPAWNS, generateCity } from './world/cityGenerator';
import { generateSigns } from './world/signs';

function boot(): void {
  const canvasHost = document.getElementById('app');
  if (!canvasHost) throw new Error('#app host not found');
  const isMobile = 'ontouchstart' in window;
  const devMem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  const constrained = isMobile && devMem != null && devMem < 4;

  const renderer = new THREE.WebGLRenderer({ antialias: !constrained, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, constrained ? 1 : 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  canvasHost.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(PALETTE.skyFog);
  scene.fog = new THREE.FogExp2(PALETTE.skyFog, FOG_DENSITY);

  const sun = new THREE.DirectionalLight(0xffffff, LIGHTING.sunIntensity);
  sun.position.set(...LIGHTING.sunPosition);
  sun.castShadow = true;
  const shadowRes = constrained ? LIGHTING.shadowMapSize / 2 : LIGHTING.shadowMapSize;
  sun.shadow.mapSize.set(shadowRes, shadowRes);
  const r = LIGHTING.shadowCameraRadius;
  sun.shadow.camera.left = -r;
  sun.shadow.camera.right = r;
  sun.shadow.camera.top = r;
  sun.shadow.camera.bottom = -r;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 200;
  sun.shadow.bias = -0.0005;
  scene.add(sun);

  const ambient = new THREE.AmbientLight(PALETTE.ambient, LIGHTING.ambientIntensity);
  scene.add(ambient);

  const hemi = new THREE.HemisphereLight(LIGHTING.hemisphereSky, LIGHTING.hemisphereGround, LIGHTING.hemisphereIntensity);
  hemi.position.set(0, 50, 0);
  scene.add(hemi);

  const camera = new THREE.PerspectiveCamera(
    CAMERA.fovMin, window.innerWidth / window.innerHeight, CAMERA.near, CAMERA.far,
  );
  camera.position.set(CAMERA.offset.x, CAMERA.offset.y, CAMERA.offset.z);
  camera.lookAt(0, CAMERA.lookHeadOffset, 0);

  const outlinePass = createOutlinePass();
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(outlinePass);
  const bloomRes = new THREE.Vector2(window.innerWidth, window.innerHeight);
  composer.addPass(new UnrealBloomPass(bloomRes, POST_PROCESSING.bloomStrength, POST_PROCESSING.bloomRadius, POST_PROCESSING.bloomThreshold));
  composer.addPass(createRageFxPass());
  composer.addPass(new OutputPass());

  const world: World = {
    scene, camera, renderer,
    input: { move: { x: 0, y: 0 }, quack: false, lastQuackAt: -Infinity, enterVehicle: false, quackCharge: 0, ragdollToggle: false },
    duck: new THREE.Group(),
    chaos: { points: 0, wanted: 0, combo: 0, comboExpiresAt: 0 },
    time: { elapsed: 0, lastFrame: 0, timeScale: 1 },
    physics: null,
    playerMode: 'foot',
    activeVehicle: null,
    vehicles: [],
    composer,
  };

  generateCity(world);
  generateSigns(world);
  initPhysics(world);
  createDuck(world);
  for (const spawn of CAR_SPAWNS) createCar(world, { position: spawn });

  // Register systems (order matters — see ARCHITECTURE.md).
  registerSystem(inputSystem);
  registerSystem(physicsSystem);
  registerSystem(movementSystem);
  registerSystem(vehicleSystem);
  registerSystem(npcSystem);
  registerSystem(quackSystem);
  registerSystem(chaosSystem);
  registerSystem(comboSystem);
  registerSystem(copSystem);
  registerSystem(ducklingSystem);
  registerSystem(objectiveSystem);
  registerSystem(duckEvolutionSystem);
  registerSystem(duckAnimationSystem);
  registerSystem(cameraSystem);
  registerSystem(dayNightSystem);
  registerSystem(neonSystem);
  registerSystem(juiceSystem);
  registerSystem(particleBurstSystem);
  registerSystem(hudSystem);
  registerSystem(duckPanelSystem);
  registerSystem(tutorialSystem);
  registerSystem(photoModeSystem);
  registerSystem(portalSystem);
  registerSystem(multiplayerSystem);

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    composer.setSize(window.innerWidth, window.innerHeight);
    updateOutlineResolution(outlinePass, window.innerWidth, window.innerHeight);
  });

  initTouchControls();
  initShareButton(world);
  initNewsTicker();
  const bootEl = document.getElementById('boot');

  if (isPortalEntry || !bootEl) {
    bootEl?.remove();
    const unlockMusic = () => {
      window.removeEventListener('keydown', unlockMusic);
      window.removeEventListener('click', unlockMusic);
      window.removeEventListener('touchstart', unlockMusic);
      startBackgroundMusic();
    };
    window.addEventListener('keydown', unlockMusic);
    window.addEventListener('click', unlockMusic);
    window.addEventListener('touchstart', unlockMusic);
    startGameLoop(world);
  } else {
    const tips = ['Ducks don\'t actually pay taxes', 'What happens in Quack City stays in Quack City', 'No ducks were harmed. Pedestrians, however...', 'A group of ducks plotting crimes is called a "quack squad"', 'Duck Norris doesn\'t do push-ups. He pushes the earth down.', 'The duck sees all. The duck judges all.', 'Quacking is not a crime. Everything after the quack is.', '100% of this game was written by an AI. Sleep well.'];
    const tipEl = document.getElementById('boot-tip');
    if (tipEl) tipEl.textContent = tips[Math.floor(Math.random() * tips.length)]!;

    const startGame = () => {
      window.removeEventListener('keydown', startGame);
      window.removeEventListener('click', startGame);
      window.removeEventListener('touchstart', startGame);
      startBackgroundMusic();
      bootEl.classList.add('fade-out');
      setTimeout(() => { bootEl.remove(); showCountdown(world); }, 600);
    };
    window.addEventListener('keydown', startGame);
    window.addEventListener('click', startGame);
    window.addEventListener('touchstart', startGame);
  }

  const hud = document.getElementById('hud');
  const controlsDiv = document.createElement('div');
  controlsDiv.id = 'hud-controls';
  controlsDiv.innerHTML = '<span>WASD</span> Move · <span>SPACE</span> Quack · <span>E</span> Car · <span>R</span> Ragdoll · <span>P</span> Photo · <span>M</span> Mute';
  const muteEl = document.createElement('div');
  muteEl.id = 'hud-mute';
  muteEl.style.cssText = 'position:absolute;top:16px;right:16px;color:#ff4444;font-family:Bangers,cursive;font-size:16px;text-shadow:1px 1px 0 #000;';
  hud?.append(muteEl, controlsDiv);
  (globalThis as unknown as { world: World }).world = world;
}
boot();
